import { Injectable } from '@angular/core';
import { CONSTANTS } from 'src/app/core/constants/constants';
import { Line } from 'src/app/core/models/line.model';
import { AnalysisCase } from 'src/app/core/models/load/analysis/analysis-case.model';
import { AssemblyMovement } from 'src/app/core/models/load/analysis/assembly-movement.model';
import { AssemblyConfiguration } from 'src/app/core/models/load/analysis/configurations/assembly-configuration.model';
import { DynamicConfiguration } from 'src/app/core/models/load/analysis/configurations/dynamic-configuration.model';
import { MovementType } from 'src/app/core/models/load/analysis/configurations/movement-type';
import { StaticConfiguration } from 'src/app/core/models/load/analysis/configurations/static-configuration.model';
import { FatigueType } from 'src/app/core/models/load/analysis/fatigue-type.enum';
import { FluidFunction } from 'src/app/core/models/load/analysis/fluid-function.model';
import { LimitState } from 'src/app/core/models/load/analysis/limit-state.enum';
import { MovementTransferType } from 'src/app/core/models/load/analysis/movement-transfer-type';
import { SolverType } from 'src/app/core/models/load/analysis/solver-type';
import { LineResultsRequester } from 'src/app/core/models/results/line-results-requester.model';
import { ResultInterval } from 'src/app/core/models/results/result-interval.model';
import { Clone } from 'src/app/shared/utils/clone';
import { IsEqual } from 'src/app/shared/utils/is-equal';
import { RemoveAllIds } from 'src/app/shared/utils/remove-all-ids/remove-all-ids';
import { DataSharingService } from '../../../data-sharing/data-sharing.service';
import { AbstractObjectService } from '../../objects/abstract-object/abstract-object';
import { AccessoriesService } from '../../objects/accessories/accessories.service';
import { MapsService } from '../../objects/maps/maps.service';
import { MaterialsService } from '../../objects/materials/materials.service';
import { TitleCopyService } from '../../objects/title-copy/title-copy.service';
import { IntervalsService } from '../../results/intervals/intervals.service';
import { SimulationManagerService } from '../../simulation-manager.service';
import { LoadService } from '../load.service';
import { AnalysisDTO } from './analysis.dto';
import { AssemblyConfigurationDTO } from './assembly-configuration.dto';
import { DynamicConfigurationDTO } from './dynamic-configuration.dto';
import { StaticConfigurationDTO } from './static-configuration.dto';

@Injectable({
  providedIn: 'root',
})
export class AnalysisService extends AbstractObjectService {
  constructor(
    protected simulationService: SimulationManagerService,
    protected accessoriesService: AccessoriesService,
    protected materialsService: MaterialsService,
    protected dataSharingService: DataSharingService,
    protected mapsService: MapsService,
    protected titleCopyService: TitleCopyService,
    private _loadService: LoadService,
    private _intervalsService: IntervalsService,
  ) {
    super(
      simulationService,
      accessoriesService,
      materialsService,
      dataSharingService,
      mapsService,
      titleCopyService,
      null,
    );

    this._objectFieldName = CONSTANTS.ANALYSIS_CASE;
  }

  create(): void {
    const newAnalysisCase: AnalysisCase = new AnalysisCase();
    const id = this.simulationService.getNewId();

    newAnalysisCase.internalId = id;
    newAnalysisCase.title = `AnalysisCase-${id}`;
    newAnalysisCase.staticSimulation = true;
    newAnalysisCase.dynamicSimulation = true;
    newAnalysisCase.assemblySimulation = false;
    newAnalysisCase.limitStateType = LimitState.ULTIMATE;
    newAnalysisCase.movementTransferType = MovementTransferType.FLOATING;
    newAnalysisCase.solverType = SolverType.COL_3D;
    newAnalysisCase.staticConfiguration = new StaticConfiguration();
    newAnalysisCase.dynamicConfiguration = new DynamicConfiguration();
    newAnalysisCase.assemblyConfiguration = new AssemblyConfiguration();
    newAnalysisCase.movementsAssembly = this.getNewAssemblyMovements();
    newAnalysisCase.fluidFunction = this.simulation.line.map((line) => {
      return {
        lineId: line.internalId,
        fluidId: -1,
        topPressure: 0,
      };
    });
    newAnalysisCase.environmentLoad = {
      waveGroupsId: this.simulation.waveGroups.map(({ internalId }) => internalId),
      currentGroupsId: this.simulation.currentGroups.map(({ internalId }) => internalId),
    };
    newAnalysisCase.postProcessing = this.simulation.results[0].lineResultsRequesters.map(
      (requester) => {
        return {
          lineId: requester.lineId,
          lineTitle: requester.lineTitle,
          requesterId: requester.internalId,
          regionResults: requester.intervals.map((interval, index) => {
            return {
              label: interval.label,
              timeHistory: false,
              envelope: false,
              fatigue: false,
              fatigueType: this.getFatigueType(interval, requester.lineId, index),
            };
          }),
        };
      },
    );
    newAnalysisCase.loadingCase = [];

    this.simulation.analysisCase.push(newAnalysisCase);
    this.mapsService.saveInAllMaps(CONSTANTS.ANALYSIS_CASE, newAnalysisCase);
    this.triggerEffects();
  }

  getFatigueType(interval: ResultInterval, lineId: number, intervalIndex: number): FatigueType {
    const line = this.mapsService.getObjectById(lineId);
    const matchingPipes = this._intervalsService.getSelectedPipes(
      interval.beginning,
      interval.end,
      line,
      intervalIndex,
    );

    if (matchingPipes.rigid.length || matchingPipes.stressJoint.length) {
      return FatigueType.rigid;
    }

    if (matchingPipes.flexible.length) {
      return FatigueType.flexible;
    }

    return null;
  }

  getNewAssemblyMovements(): AssemblyMovement[] {
    const movements = [];
    this.simulation.floating.forEach((floating) => {
      movements.push(
        this.createAssemblyMovement(floating.internalId, MovementTransferType.FLOATING),
      );
    });
    this.simulation.node.forEach((node) => {
      movements.push(this.createAssemblyMovement(node.internalId, MovementTransferType.NODE));
    });
    return movements;
  }

  createAssemblyMovement(
    referenceObjectId: number,
    movementTransferType: MovementTransferType,
  ): AssemblyMovement {
    const { internalId: fId } = this.simulation.timeFunction[0];
    return {
      referenceObjectId,
      functionId: [fId, fId, fId, fId, fId, fId],
      amplitude: [0, 0, 0, 0, 0, 0],
      movementTransferType,
    };
  }

  createFluidLoad(line: Line): void {
    this.simulation.analysisCase.forEach((analisys) => {
      const fluidLoad = {
        lineId: line.internalId,
        fluidId: -1,
        topPressure: 0,
      };
      if (analisys.fluidFunction) {
        analisys.fluidFunction.push(fluidLoad);
        return;
      }
      analisys.fluidFunction = [fluidLoad];
    });
  }

  save(analysisCaseId: number, payload: AnalysisDTO): AnalysisCase {
    const analysisCase = this.getById(analysisCaseId);
    if (!analysisCase || !payload) {
      return;
    }

    const tempAnalysis = Object.assign({ ...analysisCase }, payload);
    if (IsEqual(analysisCase, tempAnalysis)) {
      return;
    }

    Object.assign(analysisCase, payload);
    const transferTypeChanged = analysisCase.movementTransferType !== payload.movementTransferType;
    if (transferTypeChanged && payload.movementTransferType === MovementTransferType.NODE) {
      analysisCase.movementTransferType = payload.movementTransferType;
      analysisCase.dynamicConfiguration.movementType = MovementType.TIME_SERIES;
    }

    this.mapsService.updateObject(analysisCase);
    this.triggerEffects();
    return analysisCase;
  }

  saveFluidByAnalysisId(id: number, fluidFunction: FluidFunction[]): AnalysisCase {
    const analysis = this.getById(id);
    if (!analysis || !fluidFunction) {
      return;
    }
    const tempAnalysis = Object.assign({ ...analysis.fluidFunction }, fluidFunction);
    if (IsEqual(analysis, tempAnalysis)) {
      return;
    }
    analysis.fluidFunction = fluidFunction;
    this.mapsService.updateObject(analysis);
    this.triggerEffects();
    return analysis;
  }

  saveEnvironmentLoad(
    analysisCaseId: number,
    payload: {
      currentGroupsId: number[];
      waveGroupsId: number[];
    },
  ): AnalysisCase {
    const analysisCase = this.getById(analysisCaseId);
    if (!analysisCase || !payload) {
      return;
    }

    const tempPostProc = {
      currentGroupsId: payload.currentGroupsId,
      waveGroupsId: payload.waveGroupsId,
    };
    if (IsEqual(analysisCase.environmentLoad, tempPostProc)) {
      return;
    }

    analysisCase.environmentLoad = {
      currentGroupsId: payload.currentGroupsId,
      waveGroupsId: payload.waveGroupsId,
    };

    this.mapsService.updateObject(analysisCase);
    this.triggerEffects();
    return analysisCase;
  }

  savePostProcessing(
    analysisCaseId: number,
    lineId: number,
    payload: Array<{
      timeHistory: boolean;
      envelope: boolean;
      fatigue: boolean;
    }>,
  ): AnalysisCase {
    const analysisCase = this.getById(analysisCaseId);
    if (!analysisCase || !payload) {
      return;
    }

    const currentPostProcessing = analysisCase.postProcessing.find((postProcessing) => {
      return postProcessing.lineId === lineId;
    });

    const tempAnalysis = Object.assign(Clone(currentPostProcessing.regionResults), payload);
    if (IsEqual(currentPostProcessing.regionResults, tempAnalysis)) {
      return;
    }

    currentPostProcessing.regionResults = payload.map((region, index) => {
      return Object.assign(currentPostProcessing.regionResults[index], region);
    });

    this.mapsService.updateObject(analysisCase);
    this.triggerEffects();
    return analysisCase;
  }

  saveConfig(
    analysisCaseId: number,
    payload: StaticConfigurationDTO | DynamicConfigurationDTO | AssemblyConfigurationDTO,
  ): AnalysisCase {
    const analysisCase = this.getById(analysisCaseId);
    if (!analysisCase || !payload) {
      return;
    }

    const tempAnalysis = Object.assign({ ...analysisCase }, payload);
    if (IsEqual(analysisCase, tempAnalysis)) {
      return;
    }

    Object.assign(analysisCase, payload);
    this.mapsService.updateObject(analysisCase);
    this.triggerEffects();
    return analysisCase;
  }

  duplicate(analysisId: number): AnalysisCase {
    const objectArray = this.simulation.analysisCase;
    const originalObject = objectArray.find(({ internalId }) => analysisId === internalId);
    const newAnalysis: AnalysisCase = new AnalysisCase();
    const newId = this.simulationService.getNewId();

    Object.assign(newAnalysis, originalObject);
    newAnalysis.internalId = newId;
    newAnalysis.title = this.titleCopyService.getCopyFromTitle(originalObject.title);
    newAnalysis.loadingCase = originalObject.loadingCase.map((loadCase) =>
      this._loadService.duplicate(loadCase),
    );

    RemoveAllIds(newAnalysis);
    this.simulation.analysisCase.push(newAnalysis);
    this.mapsService.saveInAllMaps(CONSTANTS.ANALYSIS_CASE, newAnalysis);
    this.triggerEffects();
    return newAnalysis;
  }

  isObjectInUse(id: number): string[] | null {
    return null;
  }

  createMovementsForNewReferenceObject(
    referenceObjectId: number,
    type: MovementTransferType,
  ): void {
    this.simulation.analysisCase.forEach((analysis) => {
      analysis.movementsAssembly.push(this.createAssemblyMovement(referenceObjectId, type));

      analysis.loadingCase.forEach((loadingCase) => {
        loadingCase.movements.push(this._loadService.createMovement(referenceObjectId, type));
      });
    });
  }

  saveAssemblyMovements(
    id: number,
    movRefId: number,
    payload: number[][],
    fieldName: string,
  ): AnalysisCase {
    const analysis = this.getById(id);
    if (!analysis || !payload) {
      return;
    }

    const mov: AssemblyMovement = analysis.movementsAssembly.find(({ referenceObjectId }) => {
      return movRefId === referenceObjectId;
    });

    if (IsEqual(mov[fieldName], payload[0])) {
      return;
    }

    mov[fieldName] = payload[0];
    this.mapsService.updateObject(analysis);
    this.simulationService.changeSimulation(false);
    return analysis;
  }

  deleteLineReferences(id: number): void {
    this.simulation.analysisCase.forEach((analysis) => {
      this.deleteAnalysisFluidByLineId(analysis, id);
      this.deletePostProcessingByLineId(analysis, id);
      this.mapsService.updateObject(analysis);
    });
  }

  deletePostProcessingByLineId(analysis: AnalysisCase, id: number): void {
    analysis.postProcessing = analysis.postProcessing.filter(({ lineId }) => lineId !== id);
  }

  deleteAnalysisFluidByLineId(analysis: AnalysisCase, id: number): void {
    if (analysis.fluidFunction) {
      analysis.fluidFunction = analysis.fluidFunction.filter((fluid) => fluid.lineId !== id);
    } else {
      analysis.fluidFunction = [];
    }
  }

  deleteNodeReferencesById(ids: Set<number>): void {
    this.simulation.analysisCase.forEach((analysis) => {
      analysis.movementsAssembly = analysis.movementsAssembly.filter(
        ({ referenceObjectId }) => !ids.has(referenceObjectId),
      );

      analysis.loadingCase.forEach((loadingCase) => {
        loadingCase.movements = loadingCase.movements.filter(({ referenceObjectId: movRefId }) => {
          return !ids.has(movRefId);
        });
      });
    });
  }

  triggerEffects(): void {
    this.simulationService.changeSimulation();
    this.dataSharingService.updateErrorsLog$.next();
    this.dataSharingService.localChanges$.next();
  }

  updatePostProcessByRequester(requester: LineResultsRequester): void {
    this.simulation.analysisCase.forEach((analysis) => {
      let postProcess = analysis.postProcessing?.find(({ requesterId }) => {
        return requesterId === requester.internalId;
      });

      if (!postProcess) {
        postProcess = {
          lineId: requester.lineId,
          requesterId: requester.internalId,
          regionResults: [],
        };

        analysis.postProcessing.push(postProcess);
      }

      const { lineId } = postProcess;
      postProcess.regionResults = requester.intervals.map((interval, index) => {
        const regionResult = {
          label: interval.label,
          timeHistory: false,
          envelope: false,
          fatigue: false,
          fatigueType: this.getFatigueType(interval, lineId, index),
        };

        const region = postProcess.regionResults[index];
        if (region) {
          const fatigueIsEnabled = !!region.fatigueType;
          regionResult.timeHistory = region.timeHistory;
          regionResult.envelope = region.envelope;
          regionResult.fatigue = region.fatigue && fatigueIsEnabled;
        }

        return regionResult;
      });
      this.mapsService.updateObject(analysis);
    });
  }
}
